import React, { useState } from 'react';
import { useNavigation } from '../context/NavigationContext';
import { PUBLIC_FAQS } from '../data/publicData';
import { SectionHeading } from '../components/ui/SectionHeading';
import {
  ChevronDown,
  Search,
  HelpCircle,
  MessageCircle,
  ArrowRight,
  Mail,
} from 'lucide-react';

export const FaqPage: React.FC = () => {
  const { navigate } = useNavigation();
  const [activeCategory, setActiveCategory] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [openFaqId, setOpenFaqId] = useState<string | null>('faq-1');

  const categories = [
    { id: 'ALL', label: 'Tüm Sorular' },
    { id: 'Genel', label: 'Genel' },
    { id: 'Fiyatlandırma', label: 'Fiyatlandırma & Ödeme' },
    { id: 'Domain & SSL', label: 'Domain & SSL' },
    { id: 'Sektörel & Yat', label: 'Sektörel & Yat Modülü' },
  ];

  const filteredFaqs = PUBLIC_FAQS.filter((faq) => {
    const matchCategory = activeCategory === 'ALL' || faq.category === activeCategory;
    const q = (searchQuery || '').toLowerCase();
    const matchSearch =
      (faq.question || '').toLowerCase().includes(q) ||
      (faq.answer || '').toLowerCase().includes(q);
    return matchCategory && matchSearch;
  });

  const toggleFaq = (id: string) => {
    setOpenFaqId(openFaqId === id ? null : id);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 pt-28 sm:pt-36 pb-24">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <SectionHeading
          badge="Sıkça Sorulan Sorular"
          title="Aklınıza Takılan Tüm Soruların Cevapları"
          subtitle="Adiosoft platformu, sektör şablonları, alan adı bağlama ve fiyatlandırma hakkında merak ettikleriniz."
        />

        {/* Filter and Search */}
        <div className="mt-12 flex flex-col md:flex-row items-center justify-between gap-4 p-3 bg-slate-900/90 border border-slate-800 rounded-2xl">
          <div className="flex items-center gap-1.5 overflow-x-auto w-full md:w-auto pb-2 md:pb-0 scrollbar-none">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-3.5 py-2 rounded-xl text-xs font-semibold whitespace-nowrap transition-all ${
                  activeCategory === cat.id
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          <div className="relative w-full md:w-64">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Sorularda ara..."
              className="w-full pl-9 pr-4 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition"
            />
          </div>
        </div>

        {/* FAQs Accordion List */}
        <div className="mt-8 space-y-4">
          {filteredFaqs.length > 0 ? (
            filteredFaqs.map((faq) => {
              const isOpen = openFaqId === faq.id;

              return (
                <div
                  key={faq.id}
                  className="rounded-2xl bg-slate-900/70 border border-slate-800 overflow-hidden transition-all"
                >
                  <button
                    onClick={() => toggleFaq(faq.id)}
                    className="w-full p-5 sm:p-6 text-left flex items-center justify-between gap-4 hover:bg-slate-850 transition"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20 whitespace-nowrap">
                        {faq.category}
                      </span>
                      <h4 className="text-sm sm:text-base font-bold text-white">
                        {faq.question}
                      </h4>
                    </div>
                    <div
                      className={`w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center text-slate-400 flex-shrink-0 transition-transform duration-200 ${
                        isOpen ? 'rotate-180 bg-blue-600/20 text-blue-400' : ''
                      }`}
                    >
                      <ChevronDown className="w-4 h-4" />
                    </div>
                  </button>

                  {isOpen && (
                    <div className="px-5 pb-6 sm:px-6 text-slate-300 text-xs sm:text-sm leading-relaxed border-t border-slate-800/60 pt-4 bg-slate-900/40 animate-in fade-in duration-200">
                      {faq.answer}
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div className="text-center py-12 p-6 rounded-2xl bg-slate-900/40 border border-slate-800 text-slate-400 text-xs">
              Aramanızla eşleşen soru bulunamadı.
            </div>
          )}
        </div>

        {/* Can't find answer? Contact banner */}
        <div className="mt-16 p-8 rounded-3xl bg-slate-900 border border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-blue-600/10 border border-blue-500/20 text-blue-400 flex items-center justify-center flex-shrink-0">
              <MessageCircle className="w-6 h-6" />
            </div>
            <div>
              <h4 className="text-white font-bold text-base">
                Başka bir sorunuz mu var?
              </h4>
              <p className="text-slate-400 text-xs mt-0.5">
                Müşteri başarı ekibimiz sorularınızı yanıtlamaktan memnuniyet duyar.
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate('/iletisim')}
            className="px-6 py-3 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-semibold text-xs sm:text-sm shadow-md transition whitespace-nowrap"
          >
            Bizimle İletişime Geçin
          </button>
        </div>
      </div>
    </div>
  );
};
